import { getReferenceString } from '@medplum/core';
import type { PractitionerRole } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '@polarissleep/scheduling';
import { useEffect, useState } from 'react';
import type { Role } from '../utils/permissions';
import { ROLES } from '../utils/permissions';

const VALID_ROLES = new Set<string>(Object.values(ROLES));

export interface UseRoleResult {
  role: Role | undefined;
  isAdmin: boolean;
  loading: boolean;
}

export function useRole(): UseRoleResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const membership = medplum.getProjectMembership();
  const isAdmin = membership?.admin === true;
  const [role, setRole] = useState<Role | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    if (profile) {
      const practitionerRef = getReferenceString(profile);
      medplum
        .searchOne('PractitionerRole', {
          practitioner: practitionerRef,
        })
        .then((practitionerRole: PractitionerRole | undefined) => {
          if (!active) {
            return;
          }
          if (practitionerRole) {
            const coding = practitionerRole.code?.[0]?.coding?.find((c) => c.system === POLARIS_PROVIDER_ROLE_SYSTEM);
            if (coding?.code && VALID_ROLES.has(coding.code)) {
              setRole(coding.code as Role);
            } else {
              console.warn('[useRole] PractitionerRole found but no matching role coding.', {
                practitionerRef,
                code: practitionerRole.code,
                expectedSystem: POLARIS_PROVIDER_ROLE_SYSTEM,
                validRoles: [...VALID_ROLES],
              });
            }
          } else {
            console.warn('[useRole] No PractitionerRole found for', practitionerRef);
          }
        })
        .catch((err) => {
          console.error('[useRole] PractitionerRole search failed:', err);
        })
        .finally(() => {
          if (active) {
            setLoading(false);
          }
        });
    }

    return () => {
      active = false;
    };
  }, [medplum, profile]);

  return { role, isAdmin, loading };
}
